<!--
  One site's cement inventory.

  Nothing on this screen is typed except a stock count. Cement in is what the
  Daily Reports' delivery lines already say; cement out is every pour's m³
  times the cement per m³ its mix design declares; the balance runs from the
  latest count (what was in the silo at the end of that day) forward through
  the sheets dated after it. The rules live in lib/calc/cement.ts; this page
  only shows what they produce, newest first, with every line linking back to
  the sheet it was read off.

  Honesty on screen: a line that could not be counted is listed under
  "Not counted" with its reason rather than vanishing; lines before the first
  count are dimmed, because their balance assumes an empty silo.
-->
<script lang="ts">
  import { api } from '../../lib/api';
  import { linkHandler, location, routePath, siteIdFromQuery } from '../../lib/router';
  import { notify, notifyError, todayIso } from '../../lib/store';
  import { tonnes } from '../../lib/money';
  import type { CementInventoryResponse, CementLedgerLine } from '../../lib/types';

  const siteId = $derived(siteIdFromQuery($location.query));

  let data = $state<CementInventoryResponse | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let countDate = $state(todayIso());
  let countTonnes = $state('');
  let countNote = $state('');
  let saving = $state(false);
  let showArchived = $state(false);

  async function load(id: number) {
    loading = true;
    error = null;
    try {
      data = await api.getCementInventory(id);
    } catch (cause) {
      data = null;
      error = 'Could not load the cement inventory. Check the connection and try again.';
      notifyError(cause, 'Could not load the cement inventory');
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    if (siteId !== null) void load(siteId);
    else loading = false;
  });

  async function recordCount() {
    if (siteId === null || saving) return;
    const figure = Number(countTonnes.trim());
    if (countTonnes.trim() === '' || !Number.isFinite(figure) || figure < 0) {
      notify({ tone: 'warn', title: 'Enter the tonnes on hand' });
      return;
    }
    saving = true;
    try {
      data = await api.createCementCount({ site_id: siteId, count_date: countDate, tonnes: figure, note: countNote.trim() });
      countTonnes = '';
      countNote = '';
      notify({ tone: 'ok', title: 'Stock count recorded' });
    } catch (cause) {
      notifyError(cause, 'Could not record the count');
    } finally {
      saving = false;
    }
  }

  async function setArchived(id: number, archived: boolean) {
    try {
      data = await api.archiveCementCount({ id, archived });
      notify({ tone: 'ok', title: archived ? 'Count removed from the ledger' : 'Count restored' });
    } catch (cause) {
      notifyError(cause, archived ? 'Could not remove the count' : 'Could not restore the count');
    }
  }

  /** Newest first, the way every log in the app reads. */
  const lines = $derived(data === null ? [] : [...data.ledger.lines].reverse());

  function lineHref(line: CementLedgerLine): string | null {
    if (line.source === 'daily') return routePath.qualityDailyReport(line.record_id);
    if (line.source === 'contract') return routePath.qualityContractReport(line.record_id);
    return null;
  }

  const visibleCounts = $derived(
    data === null ? [] : data.counts.filter((count) => showArchived || count.archived === 0)
  );
  const archivedCount = $derived(data === null ? 0 : data.counts.filter((count) => count.archived === 1).length);
  const uncountedTotal = $derived(
    data === null ? 0 : data.ledger.uncounted.deliveries.length + data.ledger.uncounted.pours.length
  );

  /** kg/m³ for the mix table: three decimals would be false precision on a batching sheet. */
  const kgPerM3 = (value: number | null) => (value === null ? 'no cement row' : `${value.toFixed(value % 1 === 0 ? 0 : 1)} kg/m³`);
</script>

<div class="card">
  <div class="card__body">
    {#if siteId !== null}
      <a class="btn btn--sm" href={routePath.qualitySite(siteId)} onclick={linkHandler}>&#8592; Back to site</a>
    {:else}
      <a class="btn btn--sm" href={routePath.quality()} onclick={linkHandler}>&#8592; All sites</a>
    {/if}
    <h1 class="card__title">Cement inventory</h1>
    {#if data !== null}
      <p class="meta site">{data.site.name}</p>
    {/if}
  </div>
</div>

{#if siteId === null}
  <div class="card"><div class="card__body"><p class="meta">Open a site first: cement is kept per silo, one to a site.</p></div></div>
{:else if loading && data === null}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if error !== null && data === null}
  <div class="card"><div class="card__body"><p class="banner banner--error">{error}</p></div></div>
{:else if data !== null}
  <!-- ---- on hand ------------------------------------------------------- -->
  <div class="card">
    <div class="card__body">
      <div class="onhand">
        <span class="onhand__label">On hand</span>
        <span class="onhand__figure">{tonnes(data.ledger.on_hand_t)}</span>
        {#if data.ledger.anchored_to !== null}
          <p class="meta onhand__how">
            Counted <strong>{tonnes(data.ledger.anchored_to.tonnes)}</strong> on {data.ledger.anchored_to.count_date}.
            Since then <strong>+{tonnes(data.ledger.since_anchor.in_t)}</strong> delivered and
            <strong>&minus;{tonnes(data.ledger.since_anchor.out_t)}</strong> poured.
          </p>
        {:else if data.ledger.lines.length > 0}
          <p class="meta onhand__how">
            No stock count yet, so this assumes the silo was empty before the first sheet.
            <strong>+{tonnes(data.ledger.since_anchor.in_t)}</strong> delivered and
            <strong>&minus;{tonnes(data.ledger.since_anchor.out_t)}</strong> poured so far.
            Record a count below to anchor it.
          </p>
        {:else}
          <p class="meta onhand__how">Nothing recorded yet. Cement deliveries and pours on this site's sheets will appear here.</p>
        {/if}
      </div>
    </div>
  </div>

  <!-- ---- record a count ------------------------------------------------ -->
  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Stock count</h2>
      <p class="meta">What was actually in the silo at the end of the day. The balance restarts from it.</p>
      <div class="countform">
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={countDate} />
        </label>
        <label class="field">
          <span class="label">Tonnes on hand</span>
          <input type="text" inputmode="decimal" bind:value={countTonnes} placeholder="12.5" />
        </label>
        <label class="field countform__note">
          <span class="label">Note</span>
          <input type="text" bind:value={countNote} placeholder="Silo dipped after last load" />
        </label>
        <button class="btn btn--primary" type="button" disabled={saving} onclick={recordCount}>Record count</button>
      </div>
    </div>
  </div>

  <!-- ---- ledger -------------------------------------------------------- -->
  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Ledger</h2>
      {#if lines.length === 0}
        <p class="meta">No lines yet.</p>
      {:else}
        <div class="tablewrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Line</th><th class="num">In</th><th class="num">Out</th><th class="num">Balance</th><th></th></tr>
            </thead>
            <tbody>
              <!-- Unkeyed on purpose: two pours on one piling in one report are
                   identical in every field, so no key is unique; the list is
                   replaced wholesale whenever the inventory reloads anyway. -->
              {#each lines as line}
                <tr class:faint={line.before_first_count} class:count={line.kind === 'count'}>
                  <td class="mono">{line.date}</td>
                  <td>
                    {#if lineHref(line) !== null}
                      <a href={lineHref(line)} onclick={linkHandler}><strong>{line.label}</strong></a>
                    {:else}
                      <strong>{line.label}</strong>
                    {/if}
                    {#if line.detail}<span class="meta detail">{line.detail}</span>{/if}
                    {#if line.source === 'contract'}<span class="meta detail">Contract report</span>{/if}
                  </td>
                  <td class="num">{line.in_t === null ? '' : `+${tonnes(line.in_t)}`}</td>
                  <td class="num">{line.out_t === null ? '' : `−${tonnes(line.out_t)}`}</td>
                  <td class="num"><strong>{tonnes(line.balance_t)}</strong></td>
                  <td class="actions">
                    {#if line.kind === 'count'}
                      <button class="btn btn--sm btn--ghost" type="button" onclick={() => setArchived(line.record_id, true)}>Remove</button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        {#if data.ledger.lines.some((line) => line.before_first_count)}
          <p class="meta">Dimmed lines are before the first stock count: their balance assumes an empty silo.</p>
        {/if}
      {/if}
    </div>
  </div>

  <!-- ---- not counted ---------------------------------------------------- -->
  {#if uncountedTotal > 0}
    <div class="card">
      <div class="card__body">
        <h2 class="card__title">Not counted <span class="pill pill--warn">{uncountedTotal}</span></h2>
        <p class="meta">These lines are on the sheets but could not be turned into tonnes. Fix the sheet and they join the ledger.</p>
        <ul class="plain">
          {#each data.ledger.uncounted.deliveries as d}
            <li>
              <a href={routePath.qualityDailyReport(d.report_id)} onclick={linkHandler}><strong>{d.report_date || 'No date'}</strong> delivery of {d.material}</a>
              <span class="meta">{d.reason}</span>
            </li>
          {/each}
          {#each data.ledger.uncounted.pours as p}
            <li>
              <a href={p.source === 'daily' ? routePath.qualityDailyReport(p.report_id) : routePath.qualityContractReport(p.report_id)} onclick={linkHandler}>
                <strong>{p.report_date || 'No date'}</strong> pour {p.piling_number || ''} {p.amount_text}
              </a>
              <span class="meta">{p.reason}</span>
            </li>
          {/each}
        </ul>
      </div>
    </div>
  {/if}

  <!-- ---- mixes ----------------------------------------------------------- -->
  {#if data.mixes.length > 0}
    <div class="card">
      <div class="card__body">
        <h2 class="card__title">Cement per m³</h2>
        <p class="meta">Read off each mix design's materials sheet: the kg rows named cement, divided by the yield.</p>
        <div class="tablewrap">
          <table>
            <thead><tr><th>Mix</th><th class="num">Cement</th></tr></thead>
            <tbody>
              {#each data.mixes as mix (mix.mix_design_id)}
                <tr>
                  <td><a href={routePath.mixDesign(mix.mix_design_id)} onclick={linkHandler}>{mix.mix_code || `Mix ${mix.mix_design_id}`}</a></td>
                  <td class="num" class:muted={mix.kg_per_m3 === null}>{kgPerM3(mix.kg_per_m3)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  {/if}

  <!-- ---- counts, archived included -------------------------------------- -->
  {#if data.counts.length > 0}
    <div class="card">
      <div class="card__body">
        <h2 class="card__title">Stock counts</h2>
        {#if archivedCount > 0}
          <button class="btn btn--sm btn--ghost" type="button" onclick={() => (showArchived = !showArchived)}>
            {showArchived ? 'Hide removed' : `Show ${archivedCount} removed`}
          </button>
        {/if}
        <div class="tablewrap">
          <table>
            <thead><tr><th>Date</th><th class="num">Tonnes</th><th>Note</th><th></th></tr></thead>
            <tbody>
              {#each visibleCounts as count (count.id)}
                <tr class:faint={count.archived === 1}>
                  <td class="mono">{count.count_date}</td>
                  <td class="num">{tonnes(count.tonnes)}</td>
                  <td class="meta">{count.note}{count.archived === 1 ? ' (removed)' : ''}</td>
                  <td class="actions">
                    {#if count.archived === 1}
                      <button class="btn btn--sm btn--ghost" type="button" onclick={() => setArchived(count.id, false)}>Restore</button>
                    {:else}
                      <button class="btn btn--sm btn--ghost" type="button" onclick={() => setArchived(count.id, true)}>Remove</button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  {/if}
{/if}

<style>
  .site { margin: 0; }

  .onhand { display: grid; gap: 0.2rem; }
  .onhand__label { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--ink-soft); }
  .onhand__figure { font-size: 2.4rem; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .onhand__how { margin: 0.3rem 0 0; }
  .onhand__how strong { font-variant-numeric: tabular-nums; }

  .countform { display: grid; gap: 0.6rem; align-items: end; }
  @media (min-width: 40rem) {
    .countform { grid-template-columns: 10rem 9rem minmax(10rem, 1fr) auto; }
  }

  table a { color: inherit; text-decoration: none; }
  table a:hover strong { text-decoration: underline; }
  .detail { display: block; font-size: 0.82rem; }
  .actions { text-align: right; white-space: nowrap; }
  tr.faint td { color: var(--ink-faint); }
  tr.faint td strong { color: inherit; font-weight: 600; }
  tr.count td { background: var(--paper-soft, transparent); }
  td.num { font-variant-numeric: tabular-nums; }
  .muted { color: var(--ink-faint); }

  .plain { list-style: none; margin: 0; padding: 0; }
  .plain li { display: grid; gap: 0.1rem; padding: 0.6rem 0; }
  .plain li + li { border-top: 1px solid var(--line-soft); }
  .plain a { color: inherit; text-decoration: none; }
  .plain a:hover strong { text-decoration: underline; }
</style>
